import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "info-header" }
const _hoisted_2 = {
  key: 1,
  class: "text-regular bold title"
}
const _hoisted_3 = { class: "buttons" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "colors" }
const _hoisted_6 = { class: "text-regular visibility" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { class: "footer-buttons" }

import {
  ref, computed, watch, onMounted,
} from "vue";
import { useI18n } from "vue-i18n";
import { QuillEditor } from "@vueup/vue-quill";
import BlotFormatter from "quill-blot-formatter";

import "@vueup/vue-quill/dist/vue-quill.snow.css";
import {
  Toast, ButtonClassic, ButtonCheckbox, SelectClassic, InputClassic,
} from "@lde/core_lde_vue";
import {
  UilPen, UilCheckCircle, UilTimes, UilAngleUp, UilAngleDown, UilTrashAlt,
} from "@lde/vue-unicons";

import CommunicationModel from "@/domains/communications/communication";
import { useToast } from "@/utils/toast";

import { buildPages, Page, SubPage } from "./pages";
// #endregion

interface ColorOption {
  labelKey: string;
  value: string;
}

const COLLAPSED_STORAGE_KEY = "communications_collapsed";

/**
 * Récupère l'état de collapse depuis le localStorage.
 * Le format stocké est un objet { [id]: dateModificationAuMomentDuCollapse }.
 * Si le message a été modifié après le collapse, on le réouvre automatiquement.
 */

export default /*@__PURE__*/_defineComponent({
  ...{ name: "Communication" },
  __name: 'Communication.ts',
  props: {
    "modelValue": { required: true },
    "modelModifiers": {},
  },
  emits: /*@__PURE__*/_mergeModels(["delete", "save", "editing"], ["update:modelValue"]),
  setup(__props, { emit: __emit }) {

// #region imports =======================


// #region Général =======================

const emit = __emit;
const { t } = useI18n();
const editing = ref(false);
const message = _useModel<CommunicationModel>(__props, "modelValue");
const title = ref(message.value.titre || t("communications.titre"));
watch(message, () => {
  if (message.value.texte === "") {
    editing.value = true;
  }
}, { immediate: true, deep: true });

watch(editing, () => {
  if (editing.value) {
    // En mode édition, on force l'affichage complet du message pour faciliter la modification
    toastCollapsed.value = false;
  }
});
const toast = useToast();

// #endregion
// #region Éditeur de texte pour le message =======================
const editor = ref<InstanceType<typeof QuillEditor> | null>(null);
const content = ref(message.value.texte || "");
const toolbar = [
  ["bold", "italic", "underline", "strike"],
  ["link", "image", "video"],
  [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["clean"],
] as const;
const modules = [{
  name: "blotFormatter",
  module: BlotFormatter,
}];

// #endregion
// #region Choix des pages où afficher le message =======================

const pages = ref<Page[][]>(buildPages(message.value.pages || []));

/**
 * Au render, propage uniquement l'état de "Toutes les pages" si cette case est cochée.
 * Cela évite d'écraser la sélection réelle des pages d'un message existant.
 */
onMounted(() => {
  const allPage = pages.value.flat().find((p) => p.key === "all");
  if (allPage?.checked) {
    pages.value.forEach((pageList) => {
      pageList.forEach((p) => {
        p.checked = allPage.checked;
        p.subPages?.forEach((sp) => { sp.checked = allPage.checked; });
      });
    });
  }
});

/**
 * Gère la logique de cochage/décochage des pages et sous-pages.
 * - Si "Toutes les pages" est coché/décoché, toutes les pages suivent.
 * - Si une page parente est cochée, ses sous-pages suivent.
 * - Si toutes les sous-pages sont cochées, la page parente est cochée.
 * - Si toutes les pages individuelles sont cochées, "Toutes les pages" est coché.
 */
const gererLesPages = (page: Page | SubPage) => {
  if (page.key === "all") {
    pages.value.forEach((pageList) => {
      pageList.forEach((p) => {
        p.checked = page.checked;
        p.subPages?.forEach((sp) => { sp.checked = page.checked; });
      });
    });
    return;
  }

  if ("subPages" in page && page.subPages) {
    page.subPages.forEach((sp) => { sp.checked = page.checked; });
  } else {
    const parentPage = pages.value
      .flat()
      .find((p) => p.subPages?.some((sp) => sp.key === page.key));
    if (parentPage) {
      parentPage.checked = parentPage.subPages?.every((sp) => sp.checked) ?? false;
    }
  }

  // Mettre à jour "Toutes les pages" en fonction de l'état global
  const allPage = pages.value.flat().find((p) => p.key === "all");
  if (allPage) {
    allPage.checked = pages.value
      .flat()
      .filter((p) => p.key !== "all")
      .every((p) => p.checked && (!p.subPages || p.subPages.every((sp) => sp.checked)));
  }
};

// #endregion
// #region Choix de la couleur du message =======================

const colors: readonly ColorOption[] = [
  { labelKey: "general.bleu", value: "info" },
  { labelKey: "general.jaune", value: "warning" },
  { labelKey: "general.rouge", value: "error" },
  { labelKey: "general.vert", value: "success" },
] as const;

/** Options de couleurs traduites pour le SelectClassic */
const localizedColors = computed(() => colors.map((c) => ({ label: t(c.labelKey), value: c.value })));

const initialColor = colors.find((c) => c.value === message.value.couleur) || colors[0];
const color = ref<{ label: string; value: string }>({
  label: t(initialColor.labelKey),
  value: initialColor.value,
});

// #endregion
// #region Gestion du collapse du Toast =======================

const getInitialCollapsed = (): boolean => {
  if (!message.value?.id) return false;
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || "{}");
    const collapsedAt = stored[message.value.id];
    if (!collapsedAt) return false;
    // Si le message a été modifié après le dernier collapse, on le réouvre
    if (message.value.date_modification && new Date(message.value.date_modification) > new Date(collapsedAt)) {
      delete stored[message.value.id];
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(stored));
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const toastCollapsed = ref(getInitialCollapsed());

const toggleToast = () => {
  toastCollapsed.value = !toastCollapsed.value;
  if (!message.value?.id) return;
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || "{}");
    if (toastCollapsed.value) {
      stored[message.value.id] = new Date().toISOString();
    } else {
      delete stored[message.value.id];
    }
    localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(stored));
  } catch { /* silently ignore localStorage errors */ }
};

// #endregion
// #region Mode édition : entrer, annuler, sauvegarder =======================
let backupTitle = "";
let backupContent = "";
let backupColor = { label: "", value: "" };
let backupPages = "";

/** Entre en mode édition et sauvegarde l'état actuel pour pouvoir annuler */
const startEditing = () => {
  backupTitle = title.value;
  backupContent = content.value;
  backupColor = { ...color.value };
  backupPages = JSON.stringify(pages.value);
  editing.value = true;
  emit("editing", true);
};

/** Annule les modifications et restaure l'état précédent */
const cancelEditing = () => {
  title.value = backupTitle;
  content.value = backupContent;
  color.value = { ...backupColor };
  pages.value = backupPages ? JSON.parse(backupPages) : [];
  editing.value = false;
  emit("editing", false);
};

const _getSelectedPages = (): string[] => {
  const selectedPagesFlat = Array.from(
    pages.value.flat().reduce((acc: Set<string>, page) => {
      if (page.checked) {
        acc.add(page.key);
      }
      if (page.subPages) {
        page.subPages.forEach((sp) => {
          if (sp.checked) {
            acc.add(sp.key);
          }
        });
      }
      return acc;
    }, new Set<string>()),
  );

  if (selectedPagesFlat.length === 0) {
    // Si aucune page n'est sélectionnée, on considère que le message doit s'afficher sur toutes les pages
    selectedPagesFlat.push("all");
  }
  return selectedPagesFlat;
};

/** Sauvegarde les communications */
const save = async () => {
  // Workaround : l'alignement des images ne met pas à jour le content via v-model
  // si c'est la dernière action effectuée. On force la mise à jour.
  if (!editor.value) return;
  content.value = editor.value.getHTML();

  const res = await CommunicationModel.updateOrCreate({
    id: message.value?.id,
    titre: title.value,
    texte: content.value,
    couleur: color.value.value,
    pages: _getSelectedPages(),
  });
  if (!res) return;

  message.value = res;
  editing.value = false;
  emit("save", message.value);
  toast.success({ title: t("communications.message-sauvegarde") });
};

/** Supprime la communication */
const deleteMessage = async () => {
  if (message.value?.id) {
    await CommunicationModel.delete(message.value.id);
  }
  toast.success({ title: t("communications.message-supprime") });
  emit("delete", message.value);
};
// #endregion

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Toast), {
    class: _normalizeClass(["communication", { collapsed: toastCollapsed.value, }]),
    "hide-close-btn": "",
    variant: color.value.value,
    "show-icon": false
  }, _createSlots({
    title: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (editing.value)
          ? (_openBlock(), _createBlock(_unref(InputClassic), {
              key: 0,
              modelValue: title.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((title).value = $event)),
              label: _ctx.$t('communications.titre'),
              status: "required",
              "max-length": 255
            }, null, 8, ["modelValue", "label"]))
          : (_openBlock(), _createElementBlock("strong", _hoisted_2, _toDisplayString(title.value), 1)),
        _createElementVNode("div", _hoisted_3, [
          (!editing.value && false)
            ? (_openBlock(), _createBlock(_unref(ButtonClassic), {
                key: 0,
                variant: "special",
                color: "primary",
                icon: "right",
                size: "medium",
                label: toastCollapsed.value ? _ctx.$t('communications.developper') : _ctx.$t('communications.reduire'),
                "aria-expanded": !toastCollapsed.value,
                onClick: toggleToast
              }, {
                "right-icon": _withCtx(() => [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(toastCollapsed.value ? _unref(UilAngleDown) : _unref(UilAngleUp))))
                ]),
                _: 1
              }, 8, ["label", "aria-expanded"]))
            : _createCommentVNode("", true),
          (!editing.value && _ctx.$store.getters.isMaitreComptaRegion)
            ? (_openBlock(), _createBlock(_unref(ButtonClassic), {
                key: 1,
                variant: "special",
                color: "primary",
                icon: "left",
                size: "medium",
                "aria-label": _ctx.$t('action.modifier'),
                onClick: startEditing
              }, {
                "left-icon": _withCtx(() => [
                  _createVNode(_unref(UilPen))
                ]),
                _: 1
              }, 8, ["aria-label"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 2
  }, [
    (!toastCollapsed.value)
      ? {
          name: "content",
          fn: _withCtx(() => [
            (!editing.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "ql-editor readonly text-medium",
                  innerHTML: content.value
                }, null, 8, _hoisted_4))
              : (_openBlock(), _createBlock(_unref(QuillEditor), {
                  key: 1,
                  ref_key: "editor",
                  ref: editor,
                  content: content.value,
                  "onUpdate:content": _cache[1] || (_cache[1] = ($event: any) => ((content).value = $event)),
                  theme: "snow",
                  "content-type": "html",
                  placeholder: "Saisissez le texte ici",
                  toolbar: toolbar,
                  modules: modules
                }, null, 8, ["content"])),
            (editing.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_unref(SelectClassic), {
                      modelValue: color.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((color).value = $event)),
                      label: _ctx.$t('communications.couleur-label'),
                      options: localizedColors.value
                    }, null, 8, ["modelValue", "label", "options"])
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    _createTextVNode(_toDisplayString(_ctx.$t('communications.affichage-pages')) + " ", 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (pageList) => {
                      return (_openBlock(), _createElementBlock("ul", {
                        key: pageList[0].key
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageList, (page) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: page.key
                          }, [
                            _createVNode(_unref(ButtonCheckbox), {
                              modelValue: page.checked,
                              "onUpdate:modelValue": [($event: any) => ((page.checked) = $event), ($event: any) => (gererLesPages(page))],
                              label: _ctx.$t(page.labelKey)
                            }, null, 8, ["modelValue", "onUpdate:modelValue", "label"]),
                            (page.subPages)
                              ? (_openBlock(), _createElementBlock("ul", _hoisted_7, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(page.subPages, (subPage) => {
                                    return (_openBlock(), _createElementBlock("li", {
                                      key: subPage.key
                                    }, [
                                      _createVNode(_unref(ButtonCheckbox), {
                                        modelValue: subPage.checked,
                                        "onUpdate:modelValue": [($event: any) => ((subPage.checked) = $event), ($event: any) => (gererLesPages(subPage))],
                                        label: _ctx.$t(subPage.labelKey)
                                      }, null, 8, ["modelValue", "onUpdate:modelValue", "label"])
                                    ]))
                                  }), 128))
                                ]))
                              : _createCommentVNode("", true)
                          ]))
                        }), 128))
                      ]))
                    }), 128))
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_unref(ButtonClassic), {
                      variant: "solid",
                      color: "error",
                      icon: "left",
                      size: "medium",
                      label: _ctx.$t('action.supprimer'),
                      onClick: deleteMessage
                    }, {
                      "left-icon": _withCtx(() => [
                        _createVNode(_unref(UilTrashAlt))
                      ]),
                      _: 1
                    }, 8, ["label"]),
                    (message.value.id)
                      ? (_openBlock(), _createBlock(_unref(ButtonClassic), {
                          key: 0,
                          variant: "solid",
                          label: _ctx.$t('action.annuler'),
                          color: "secondary",
                          icon: "right",
                          onClick: cancelEditing
                        }, {
                          "right-icon": _withCtx(() => [
                            _createVNode(_unref(UilTimes))
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(ButtonClassic), {
                      variant: "solid",
                      label: _ctx.$t('action.enregistrer'),
                      color: "primary",
                      icon: "right",
                      onClick: save
                    }, {
                      "right-icon": _withCtx(() => [
                        _createVNode(_unref(UilCheckCircle))
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ])
                ], 64))
              : _createCommentVNode("", true)
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["variant", "class"]))
}
}

})